import React from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import usePerx3Redesign from 'hooks/usePerx3Redesign';

import { visuallyHidden } from '../visuallyHidden';

export type GateStatus = 'passed' | 'warning' | 'failed';

/**
 * `status` is the shipped rail: every step is coloured by its own verdict. It stays the default, so
 * `HandoffCheckList` — the only other caller — renders exactly as before.
 *
 * `flow` is what `589-29991` draws on the Growth Planner Handoff Readiness card: the chain is
 * NEUTRAL and only its TERMINAL step is coloured, because that rail restates the funnel and the last
 * step is its output rather than one more verdict. The two lines are also emphasised the other way
 * round there — the label carries the weight, not the figure — and both are centred.
 */
export type GateStepperVariant = 'status' | 'flow';

export interface IGate {
  label: string;
  /** Pre-formatted by the caller — a gate reads as a population count or a rate. */
  value: string;
  passed: boolean;
  /** Omit for the two-valued rail; `status` wins over `passed` when both are supplied. */
  status?: GateStatus;
}

export interface IGateStepperProps {
  gates: IGate[];
  /** Defaults to the shipped `status` rail; see `GateStepperVariant`. */
  variant?: GateStepperVariant;
  className?: string;
}

const GLOW_OPACITY = 0.4;

const STATUS_HUE: Record<GateStatus, string> = {
  passed: tokens.color.positive,
  warning: tokens.color.warning,
  failed: tokens.color['border-subtle']
};

const HAS_GLOW: Record<GateStatus, boolean> = {
  passed: true,
  warning: true,
  failed: false
};

const STATUS_LABEL_KEY: Record<GateStatus, string> = {
  passed: 'Passed',
  warning: 'Warning',
  failed: 'Failed'
};

const statusOf = (gate: IGate): GateStatus => gate.status ?? (gate.passed ? 'passed' : 'failed');

// A gate that never declared a `status` keeps the shipped two-valued wording, so the call sites
// reviewed in P2 read identically to assistive tech.
const statusLabelKey = (gate: IGate): string =>
  gate.status ? STATUS_LABEL_KEY[gate.status] : gate.passed ? 'Passed' : 'NotPassed';

const Stepper = styled.ol`
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;
  list-style: none;
`;

/* 589-29991 draws its six chips at ONE width, flush at both ends: they sit at x 664, 773, 881, 990,
   1098 and 1206 and end at 747, 855, 963, 1072, 1180 and 1288, so each is ~83.8 of the 628 column
   with five ~25px gaps between them. The shipped rail cannot reproduce that, because the connector
   is a child of the item and only the LAST item has none -- so the last chip inherits the
   connector's width and renders 107 against its siblings' 80 (measured, both engines, 1440 and
   1280). On the flow rail the connector is therefore taken OUT of flow and parked in a margin, which
   leaves every item the same box and keeps the row flush. 24 is the scale step nearest the measured
   25; the 4/8/12/16/20/24/32 scale has nothing at 25.
   The status rail keeps the in-flow connector it was built with. */
const FLOW_GAP = tokens.space['24'];

const GateItem = styled.li<{ $variant: GateStepperVariant }>`
  display: flex;
  align-items: stretch;
  flex: 1 1 0;
  min-width: 0;

  ${({ $variant }) =>
    $variant === 'flow'
      ? `
    position: relative;

    & + & {
      margin-left: ${FLOW_GAP};
    }
  `
      : ''}
`;

// On the flow rail only the terminal step is lit, so the hue and the glow are decided by POSITION
// there and by verdict on the shipped rail. Read off the payload's own length, so a rail with one
// more gate lights the new last step rather than a fixed index.
const nodeHue = (variant: GateStepperVariant, status: GateStatus, terminal: boolean): string => {
  if (variant === 'flow') {
    return terminal ? tokens.color.positive : tokens.color['flow-node-border'];
  }

  return STATUS_HUE[status];
};

const nodeGlows = (variant: GateStepperVariant, status: GateStatus, terminal: boolean): boolean =>
  variant === 'flow' ? terminal : HAS_GLOW[status];

const Node = styled.div<{
  $status: GateStatus;
  $variant: GateStepperVariant;
  $terminal: boolean;
  $perx3: boolean;
}>`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  /* 589-29992, the flow step's own Properties panel: 82.31 wide, HUG 41 high, padding 10 top and
     bottom / 9 left and right, corner radius/9, stroke weight/1. The rail had been drawn on the
     status step's 8/12 padding at radius 10, which is the slide-out's radius and not this one, and
     it stood 57 tall against the file's 41. */
  padding: ${({ $variant }) =>
    $variant === 'flow' ? '10px 9px' : `${tokens.space['8']} ${tokens.space['12']}`};
  /* PERX3-REDESIGN: the flow rail drops its outline — the lit step is already stated by the inner
     glow below and by the visually-hidden status text, so the hue was a third channel and the only
     tinted border on this card. The box is kept at 1px so the step's measured 41 height and its
     ~83.8 width do not move; only the paint goes. Off the flag the hue is written as before. */
  border: 1px solid
    ${({ $variant, $status, $terminal, $perx3 }) =>
      $perx3 && $variant === 'flow' ? 'transparent' : nodeHue($variant, $status, $terminal)};
  border-radius: ${({ $variant }) => ($variant === 'flow' ? tokens.radius['9'] : tokens.radius.sm)};
  background: ${tokens.color['surface-card']};
  text-align: ${({ $variant }) => ($variant === 'flow' ? 'center' : 'left')};

  /* A cleared gate also carries an inner glow in the library. A token is a CSS custom-property
     reference, so the alpha cannot be folded into the value: the glow is its own layer carrying an
     opacity, per ConfidenceBadge. */
  ${({ $status, $variant, $terminal }) =>
    nodeGlows($variant, $status, $terminal)
      ? `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border-radius: inherit;
      box-shadow: inset 0 0 8px ${nodeHue($variant, $status, $terminal)};
      opacity: ${GLOW_OPACITY};
      pointer-events: none;
    }
  `
      : ''}
`;

/* The two lines separate by INK on the flow rail, not by weight: `589-29993` is one text node
   carrying both, and its panel prints a single Inter 400 / 8px / centred style rather than `Mixed`.
   Sampled at x=700 on `589-29991`, the figure reads a grey and its name the file's near-white, so
   the name looks heavier at 8px without being a heavier cut. The shipped rail keeps the 18/21.6
   figure over an 11/17 name that it was built with. */
const Value = styled.span<{ $variant: GateStepperVariant }>`
  display: block;
  color: ${({ $variant }) =>
    $variant === 'flow' ? tokens.color['text-secondary'] : tokens.color['text-primary']};
  font-size: ${({ $variant }) =>
    $variant === 'flow' ? tokens.type['flow-step-size'] : tokens.type['card-title-size']};
  font-weight: ${({ $variant }) =>
    $variant === 'flow' ? tokens.type['flow-step-weight'] : tokens.type['stat-weight']};
  line-height: ${({ $variant }) =>
    $variant === 'flow' ? tokens.type['flow-step-line'] : tokens.type['card-title-line']};
`;

const Label = styled.span<{ $variant: GateStepperVariant }>`
  display: block;
  color: ${({ $variant }) =>
    $variant === 'flow' ? tokens.color['text-primary'] : tokens.color['text-secondary']};
  font-size: ${({ $variant }) =>
    $variant === 'flow' ? tokens.type['flow-step-size'] : tokens.type['label-size']};
  font-weight: ${({ $variant }) =>
    $variant === 'flow' ? tokens.type['flow-step-weight'] : tokens.type['body-weight']};
  line-height: ${({ $variant }) =>
    $variant === 'flow' ? tokens.type['flow-step-line'] : tokens.type['label-line']};
`;

const GateStatusText = styled.span`
  ${visuallyHidden}
`;

// The flow rail's connector samples #AEBBD0 on `589-29991`, which is --is-text-secondary exactly;
// the shipped rail keeps the dimmer --is-text-muted it was built with.
const Arrow = styled.span<{ $variant: GateStepperVariant }>`
  flex: 0 0 auto;
  align-self: center;
  padding: 0 ${tokens.space['8']};
  color: ${({ $variant }) =>
    $variant === 'flow' ? tokens.color['text-secondary'] : tokens.color['text-muted']};
  font-size: ${tokens.type['body-size']};
  line-height: ${tokens.type['body-line']};

  ${({ $variant }) =>
    $variant === 'flow'
      ? `
    position: absolute;
    top: 50%;
    left: 100%;
    width: ${FLOW_GAP};
    padding: 0;
    transform: translateY(-50%);
    text-align: center;
  `
      : ''}
`;

const GateStepper: React.FC<IGateStepperProps> = ({ gates, variant = 'status', className }) => {
  const { t } = useMultiLanguageTranslation();
  const perx3 = usePerx3Redesign();
  const chain = Array.isArray(gates) ? gates : [];

  if (chain.length === 0) {
    return null;
  }

  return (
    <Stepper className={className} data-variant={variant} data-testid="gate-stepper">
      {chain.map((gate, position) => {
        const terminal = position === chain.length - 1;

        return (
          <GateItem key={gate.label} $variant={variant} data-testid="gate-stepper-item">
            <Node
              $status={statusOf(gate)}
              $variant={variant}
              $terminal={terminal}
              $perx3={perx3}
              data-passed={gate.passed}
              data-status={statusOf(gate)}
              data-terminal={terminal}
              data-testid="gate-stepper-node"
            >
              <Value $variant={variant} data-testid="gate-stepper-value">
                {gate.value}
              </Value>
              <Label $variant={variant} data-testid="gate-stepper-label">
                {gate.label}
              </Label>
              {/* The gate state is otherwise carried by hue alone (WCAG 1.4.1) — and on the flow
                  rail hue does not carry it at all, so this is the only place it is stated. */}
              <GateStatusText data-testid="gate-stepper-status">
                {t(`${TCustomerInsights}.Gates.${statusLabelKey(gate)}`)}
              </GateStatusText>
            </Node>
            {terminal ? null : (
              <Arrow $variant={variant} aria-hidden="true" data-testid="gate-stepper-arrow">
                &#8594;
              </Arrow>
            )}
          </GateItem>
        );
      })}
    </Stepper>
  );
};

export default GateStepper;
